// =============================================================================
// Colour maths for theming. Used by both the browser (theme.js) and the Node
// build scripts, so a brand colour is interpreted identically everywhere.
//
// This exists because a brand colour is chosen for a logo, not for a user
// interface. A mid-tone orange that looks right on a sign is unreadable behind
// white text. Rather than hand-picking shades per school, the theme derives
// what it needs and checks the contrast.
// =============================================================================

export function hexToRgb(hex) {
  const h = String(hex).replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
}

export const rgbToHex = (rgb) =>
  '#' + rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');

// WCAG relative luminance.
export function luminance(hex) {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// WCAG contrast ratio, 1–21.
export function contrast(a, b) {
  const la = luminance(a), lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// Whichever of white/near-black is more readable on this background.
// Near-black rather than pure black: it matches the logo wordmark and is
// easier on the eye at a desk under fluorescent light.
export const INK = '#16202b';
export function bestTextOn(bg) {
  return contrast(bg, '#ffffff') >= contrast(bg, INK) ? '#ffffff' : INK;
}

export function mix(hex, target, amount) {
  const a = hexToRgb(hex), b = hexToRgb(target);
  return rgbToHex(a.map((v, i) => v + (b[i] - v) * amount));
}

export const darken = (hex, amount) => mix(hex, '#000000', amount);
export const lighten = (hex, amount) => mix(hex, '#ffffff', amount);

// Darken a colour just far enough to be readable as text/icons on a light
// background. A brand orange sits around 2.5:1 on white — well under the 4.5:1
// WCAG AA threshold — so links and active tabs in raw brand colour would be a
// genuine accessibility failure, not a matter of taste.
export function readableOn(brand, background = '#ffffff', target = 4.5) {
  let out = brand;
  for (let step = 0; step <= 20; step++) {
    out = darken(brand, step * 0.05);
    if (contrast(out, background) >= target) return out;
  }
  return out;
}

// A tint for banner/pill backgrounds that keeps the brand hue but stays light
// enough for dark text.
export const tint = (hex, amount = 0.88) => lighten(hex, amount);
